/**
 * One place for how a mention chip looks — the old web's
 * `mentionChipPresentation.ts`, with its Tailwind class strings replaced by
 * class names `brand.css` styles. The same chip renders in four places (the
 * live editor node view, the user's message, the assistant's markdown via
 * `rehypeMentions`, and `CustomMention.renderHTML` for the clipboard), and
 * they drift apart the moment each carries its own template.
 *
 * Two overflow axes, as the reference: more than `MINOR_CHIP_INLINE_THRESHOLD`
 * parameters collapse the tail into a `+N` chip (`splitParamsForDisplay`), and
 * a chip group wider than its line wraps (the wrapper is `inline-flex` +
 * `flex-wrap`; each chip is `nowrap`).
 */
import type { MentionParameter } from "./mentionUtils";

export const MENTION_WRAPPER_CLASS = "revit-mention";
export const MAJOR_CHIP_INTERACTIVE_CLASS = "revit-mention-major is-interactive";
export const MAJOR_CHIP_DECORATIVE_CLASS = "revit-mention-major is-decorative";
export const MINOR_CHIP_CLASS = "revit-mention-minor";
export const MINOR_CHIP_OVERFLOW_CLASS = "revit-mention-overflow";

export const MINOR_CHIP_INLINE_THRESHOLD = 3;
export const MINOR_CHIP_VISIBLE_WHEN_OVERFLOW = 3;

export function getMajorChipText(label: string): string {
  return `@${label}`;
}

export function getMajorChipTooltip(type: string, id: string): string {
  return `${type} (ID: ${id})`;
}

/** Just the name: scope and element id are implementation detail, not for hover. */
export function getMinorChipTooltip(param: MentionParameter): string {
  return param.name;
}

export function getOverflowTooltip(overflow: MentionParameter[]): string {
  return overflow.map((p) => p.name).join(", ");
}

/**
 * Blue when the item has parameters to pick from, slate when it is a pure
 * reference (Category, Level…). `undefined` — no cache to ask — is the
 * optimistic blue, so a cold first paint matches the live data flow.
 */
export function getMajorChipClass(hasExtractable?: boolean): string {
  if (hasExtractable === false) return MAJOR_CHIP_DECORATIVE_CLASS;
  return MAJOR_CHIP_INTERACTIVE_CLASS;
}

/**
 * Does the item carry any parameter arrays? Items like `Category` come through
 * without any, and that absence — not a hardcoded type list — is what makes a
 * chip decorative, so a new reference-only type needs no client change.
 */
export function hasExtractableParameters(
  item:
    | {
        familyParameters?: ReadonlyArray<unknown> | null;
        typeParameters?: ReadonlyArray<unknown> | null;
        instanceParameters?: ReadonlyArray<unknown> | null;
        cadLayers?: ReadonlyArray<unknown> | null;
        cadBlocks?: ReadonlyArray<unknown> | null;
      }
    | null
    | undefined,
): boolean {
  if (!item) return false;
  return (
    (item.familyParameters?.length ?? 0) +
      (item.typeParameters?.length ?? 0) +
      (item.instanceParameters?.length ?? 0) +
      (item.cadLayers?.length ?? 0) +
      (item.cadBlocks?.length ?? 0) >
    0
  );
}

export function splitParamsForDisplay(params: MentionParameter[]): { visible: MentionParameter[]; overflow: MentionParameter[] } {
  if (params.length <= MINOR_CHIP_INLINE_THRESHOLD) return { visible: params, overflow: [] };
  return {
    visible: params.slice(0, MINOR_CHIP_VISIBLE_WHEN_OVERFLOW),
    overflow: params.slice(MINOR_CHIP_VISIBLE_WHEN_OVERFLOW),
  };
}
